/**
 * Channel and sub-channel names come from the server-owned `channel`
 * namespace (`public/locales/<lng>/channel.json`), keyed by the raw id the
 * server sends (`facebook`, `google_search`, `yahooJapanSearchAds`).
 * `filter_<id>` holds the label used when the channel is a filter option,
 * which for umbrella channels is the platform family (`filter_facebook` =
 * Meta) and is not derived from the full name.
 *
 * Mirrors adriel-fe-react `hooks/useChannelName.ts`; keep the two in step.
 */

const toRaw = (id) => (id === undefined || id === null ? '' : String(id));

export const resolveChannelName = ({ id, fallback, exists, t }) => {
    const raw = toRaw(id);
    if (!raw) return fallback ?? '';

    // Old Facebook placement rows carry capitalised ids (`Instagram`); the
    // server keys are lower case.
    const lower = raw.toLowerCase();
    const hit = [`CHANNEL.${raw}`, `CHANNEL.${lower}`].find(exists);

    return hit ? t(hit) : fallback ?? raw;
};

export const resolveChannelFilterName = ({ id, fallback, exists, t }) => {
    const raw = toRaw(id);
    if (!raw) return fallback ?? '';

    const hit = [`CHANNEL.filter_${raw}`].find(exists);

    // A caller that passes a fallback has already resolved the full name (and
    // any account-level display name) itself; only resolve it here when not.
    return hit ? t(hit) : fallback ?? resolveChannelName({ id: raw, exists, t });
};

// Component-bound helpers: call as `getChannelName.call(this, id, fallback)`
// from an Options API component or mixin (same pattern as utils/field.js).
// An empty translation counts as missing, like `translateOr`.
const i18nOf = (vm) => ({
    exists: (key) => vm.$te(key) && vm.$t(key) !== '',
    t: (key) => vm.$t(key),
});

export const getChannelName = function (id, fallback) {
    return resolveChannelName({ id, fallback, ...i18nOf(this) });
};

export const getChannelFilterName = function (id, fallback) {
    return resolveChannelFilterName({ id, fallback, ...i18nOf(this) });
};
